import useSWR from "swr";

import SWR_KEYS from "@/constants/swr-keys";
import { IOrder } from "@/interfaces/order.interfaces";
import orderService from "@/services/order.service";
import { FilterProperty, IFilter } from "@/types/filter.types";
import { PaginatedResponse, RequestPagination, RequestsBaseReturn } from "@/types/requests.types";

export type UseGetOrdersReturn = RequestsBaseReturn<PaginatedResponse<IOrder> | null>;

export interface UseGetOrdersProps extends RequestPagination {
  filters: IFilter<IOrder>[];
}

type GetAllSwrParams = [string, number, number, IFilter<IOrder>[]];

const normalizeSingleFilterFormData = <T>(
  formData: IFilter<T>[],
  defaultProperty: FilterProperty<T>
): IFilter<T>[] => {
  if (!formData || formData.length === 0) return formData;

  const hasAll = formData.some((f) => f.property === "all");

  if (!hasAll) {
    return formData.map((filter) =>
      filter.property === "all" ? { ...filter, property: defaultProperty } : filter
    );
  }

  const allIndex = formData.findIndex((f) => f.property === "all");
  const allFilter = formData[allIndex];

  const newArr: IFilter<T>[] = [{ ...allFilter, property: defaultProperty }];

  formData.forEach((f) => {
    if (f.filterType === "notEquals") {
      newArr.push(f);
    }
  });

  return newArr;
};

const useGetOrders = ({ pageNumber, pageSize, filters }: UseGetOrdersProps): UseGetOrdersReturn => {
  const normalizedFilter = normalizeSingleFilterFormData(filters, "orderNumber");

  const fetcher = useSWR<PaginatedResponse<IOrder>, Error, GetAllSwrParams>(
    [SWR_KEYS.getOrders, pageNumber, pageSize, normalizedFilter],
    ([, _pageNumber, _pageSize, _filters]) =>
      orderService.filter(_filters, { pageNumber: _pageNumber, pageSize: _pageSize }),
    { revalidateOnFocus: false }
  );

  const { data = null, isValidating: isLoading, error } = fetcher;

  return { data, isLoading, error };
};

export default useGetOrders;
